import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { SpaceAccountAppCalloutSummarySchema } from "@/domains/openapi/types";
import { Uuid } from "@/domains/global/identifiers";
import { Button } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { SpaceAccountAppCalloutsTable } from "@/pages/space-account-app-callouts/shared/space-account-app-callouts-table";

export const SpaceAccountSpaceAccountAppCalloutsPage: FC = () => {
  const navigate = useNavigate();
  const { spaceAccountId } = useParams<{ spaceAccountId: Uuid }>();

  if (!spaceAccountId) {
    return <div>Invalid ID</div>;
  }

  const pageSize = 1000;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["space-account-space-account-app-callouts", spaceAccountId],
    queryFn: () =>
      api.post("/internal/v1/space-account-app-callouts/search", {
        body: {
          filter_by_space_account_id: spaceAccountId,
          limit: pageSize,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const handleViewSpaceAccountAppCallout = (id: string) => {
    navigate(
      atlasRoutes.spaceAccountAppCalloutsView({ params: { spaceAccountAppCalloutId: id } }).path
    );
  };

  const handleViewSpaceAccount = () => {
    navigate(atlasRoutes.spaceAccountsView({ params: { spaceAccountId } }).path);
  };

  const tableData = useMemo<SpaceAccountAppCalloutSummarySchema[]>(
    () => data?.data?.results ?? [],
    [data]
  );

  return (
    <Container>
      <h1>Space Account App Callouts</h1>

      <Button variant="outline" onClick={() => handleViewSpaceAccount()}>
        View Space Account
      </Button>
      <br />
      <br />

      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching space account app callouts: {error.message}</div>}

      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <SpaceAccountAppCalloutsTable
          data={tableData}
          onViewSpaceAccountAppCallout={handleViewSpaceAccountAppCallout}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && <div>No space account app callouts found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;
